import { useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import {
  Upload, Trash2, FileSpreadsheet, X, Info, ChevronRight, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtNum = (v) => (v == null ? '—' : Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 2 }));
const fmtWeek = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}`;
  } catch { return iso; }
};
const fmtTs = (ts) => {
  if (ts == null) return '';
  try {
    const d = new Date(ts);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}.${mm}.${yy}`;
  } catch { return String(ts); }
};
const fmtTsFull = (ts) => {
  if (ts == null) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return String(ts); }
};

// Палитра цветов для серий поставщиков (deterministic by index)
const SUPPLIER_PALETTE = [
  '#2563eb', '#16a34a', '#dc2626', '#9333ea', '#ea580c',
  '#0891b2', '#ca8a04', '#db2777', '#475569', '#65a30d',
  '#7c3aed', '#0d9488', '#b91c1c', '#1d4ed8', '#15803d',
];
const colorFor = (idx) => SUPPLIER_PALETTE[idx % SUPPLIER_PALETTE.length];

const BidsTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const items = payload.filter(p => p.value != null);
  if (!items.length) return null;
  const ts = items[0]?.payload?.ts;
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-md px-3 py-2 text-[11px]">
      <div className="text-slate-500 mb-1">{fmtTsFull(ts)}</div>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: it.color }} />
          <span className="text-slate-800 font-medium truncate max-w-[260px]" title={it.name}>{it.name}</span>
          <span className="ml-auto text-slate-900 font-semibold tabular-nums">{fmtNum(it.value)}</span>
        </div>
      ))}
    </div>
  );
};


export default function ExternalAnalyticsTab() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [selectedProducts, setSelectedProducts] = useState([]); // мульти-выбор
  const [productMenuOpen, setProductMenuOpen] = useState(false);
  const productMenuRef = useRef(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [bySupplier, setBySupplier] = useState(false);
  const [supplier, setSupplier] = useState('');

  const [data, setData] = useState({ weeks: [], tender_count: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // {done, total}
  const [expandedWeeks, setExpandedWeeks] = useState(() => new Set());
  const [expandedLots, setExpandedLots] = useState(() => new Set());

  // Авто-импорт из папки
  const [watch, setWatch] = useState({
    folder_path: '', enabled: false, poll_interval: 60,
    last_scan_at: null, last_scan_imported: 0, last_scan_skipped: 0,
    last_scan_error: null, watcher_running: false, next_scan_at: null,
  });
  const [watchFolderInput, setWatchFolderInput] = useState('');
  const [watchIntervalInput, setWatchIntervalInput] = useState(60);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  // Браузер серверных папок
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseData, setBrowseData] = useState(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState('');

  const reloadMeta = async () => {
    const [{ data: b }, { data: p }, { data: s }] = await Promise.all([
      api.get('/admin/archive/external-uploads').catch(() => ({ data: [] })),
      api.get('/admin/archive/external-products').catch(() => ({ data: [] })),
      api.get('/admin/archive/external-suppliers').catch(() => ({ data: [] })),
    ]);
    setBatches(Array.isArray(b) ? b : []);
    setProducts(Array.isArray(p) ? p : []);
    setSuppliers(Array.isArray(s) ? s : []);
  };

  const reloadWatch = async () => {
    try {
      const { data } = await api.get('/admin/archive/external-watch-settings');
      setWatch(data || {});
      setWatchFolderInput(data?.folder_path || '');
      setWatchIntervalInput(Number(data?.poll_interval || 60));
    } catch {/* ignore */}
  };

  useEffect(() => { reloadMeta(); reloadWatch(); }, []);

  // Подтягиваем статус воркера и список загрузок каждые 15с, пока вкладка открыта.
  useEffect(() => {
    const t = setInterval(() => { reloadWatch(); reloadMeta(); }, 15000);
    return () => clearInterval(t);
  }, []);

  const saveWatchSettings = async (overrides = {}) => {
    try {
      const payload = {
        folder_path: watchFolderInput,
        poll_interval: Number(watchIntervalInput) || 60,
        enabled: watch.enabled,
        ...overrides,
      };
      const { data } = await api.put('/admin/archive/external-watch-settings', payload);
      setWatch(data || {});
      toast.success('Настройки папки сохранены');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Не удалось сохранить настройки');
    }
  };

  const scanNow = async () => {
    if (!watch.folder_path) {
      toast.error('Сначала укажите путь к папке и сохраните настройки');
      return;
    }
    setScanBusy(true); setScanResult(null);
    try {
      const { data } = await api.post('/admin/archive/external-scan-now');
      setScanResult(data);
      const imp = data?.imported || 0;
      const skp = data?.skipped || 0;
      if (data?.error) {
        toast.error(`Ошибка: ${data.error}`);
      } else if (imp > 0) {
        toast.success(`Импортировано: ${imp}, пропущено: ${skp}`);
        await reloadMeta();
      } else {
        toast.message ? toast.message(`Новых файлов не найдено (пропущено: ${skp})`) : toast.success(`Новых файлов нет (пропущено: ${skp})`);
      }
      await reloadWatch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Не удалось запустить сканирование');
    } finally {
      setScanBusy(false);
    }
  };

  const openBrowser = async (startPath = '') => {
    setBrowseOpen(true);
    await loadBrowsePath(startPath || watchFolderInput || '');
  };
  const loadBrowsePath = async (p) => {
    setBrowseLoading(true); setBrowseError('');
    try {
      const { data } = await api.get('/admin/archive/external-watch-browse', {
        params: p ? { path: p } : {},
      });
      setBrowseData(data);
    } catch (e) {
      setBrowseError(e?.response?.data?.detail || 'Не удалось открыть папку');
    } finally {
      setBrowseLoading(false);
    }
  };
  const chooseBrowsedPath = () => {
    if (browseData?.path) {
      setWatchFolderInput(browseData.path);
      setBrowseOpen(false);
    }
  };

  // Загрузка данных при изменении фильтров
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (selectedProducts.length === 1) {
          params.product = selectedProducts[0];
        } else if (selectedProducts.length > 1) {
          params.products = selectedProducts.join(',');
        }
        if (bySupplier && supplier) params.supplier = supplier;
        const { data } = await api.get('/admin/archive/external-analytics', { params });
        if (!cancelled) setData(data || { weeks: [], tender_count: 0 });
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Не удалось загрузить аналитику');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, selectedProducts, bySupplier, supplier, batches.length]);

  // Закрытие меню выбора товаров по клику снаружи
  useEffect(() => {
    const handler = (e) => {
      if (productMenuRef.current && !productMenuRef.current.contains(e.target)) {
        setProductMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleProduct = (name) => {
    setSelectedProducts(prev =>
      prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]
    );
  };
  const clearProducts = () => setSelectedProducts([]);

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const xlsx = Array.from(files).filter(f => (f.name || '').toLowerCase().endsWith('.xlsx') && !f.name.startsWith('~$'));
    let inserted = 0;
    let imported = 0;
    let duplicate = 0;
    let errs = 0;
    let processed = 0;
    for (const f of xlsx) {
      try {
        const form = new FormData();
        form.append('file', f);
        const { data } = await api.post('/admin/archive/external-uploads', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data?.duplicate) {
          duplicate += 1;
        } else {
          inserted += data?.rows_inserted || 0;
          imported += 1;
        }
      } catch (e) {
        errs += 1;
        toast.error(`${f.name}: ${e?.response?.data?.detail || 'ошибка загрузки'}`);
      }
      processed += 1;
      setUploadProgress({ done: processed, total: xlsx.length });
    }
    setUploading(false);
    setUploadProgress(null);
    const parts = [];
    if (imported) parts.push(`импортировано ${imported}`);
    if (inserted) parts.push(`+${inserted} строк`);
    if (duplicate) parts.push(`пропущено ${duplicate} (уже было)`);
    if (errs) parts.push(`ошибок: ${errs}`);
    if (parts.length) toast.success(parts.join(', '));
    if (imported > 0 || errs === 0) await reloadMeta();
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  const deleteBatch = async (id) => {
    if (!window.confirm('Удалить эту загрузку и все её ставки из аналитики?')) return;
    try {
      await api.delete(`/admin/archive/external-uploads/${id}`);
      toast.success('Загрузка удалена');
      reloadMeta();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ошибка');
    }
  };

  const chartData = useMemo(() => (data.weeks || []).map(w => ({
    week: fmtWeek(w.week_start),
    week_start: w.week_start,
    winning: w.weighted_winning,
    first_winner: w.weighted_first_winner,
    tenders: w.tender_count,
    lots: w.tenders || [],
  })), [data]);

  // Точки «динамика всех ставок»: каждая строка = момент времени,
  // у строки заполнены только те поставщики, которые сделали ставку в этот момент.
  const bidSeries = useMemo(() => {
    const bids = data.bids || [];
    if (!bids.length) return { points: [], suppliers: [], domain: [0, 'auto'] };
    const supplierNames = Array.from(new Set(bids.map(b => b.supplier))).sort();
    const points = bids.map(b => {
      const row = { ts: b.ts };
      row[b.supplier] = b.price;
      // Метаданные для tooltip/легенды
      return row;
    });
    const prices = bids.map(b => b.price).filter(v => v != null);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const pad = (max - min) * 0.08 || max * 0.05 || 100;
    const domain = [Math.max(0, Math.floor(min - pad)), Math.ceil(max + pad)];
    return { points, suppliers: supplierNames, domain };
  }, [data]);

  const tsDomain = useMemo(() => {
    const pts = bidSeries.points;
    if (!pts.length) return ['auto', 'auto'];
    return [pts[0].ts, pts[pts.length - 1].ts];
  }, [bidSeries]);


  const toggleWeek = (wk) => setExpandedWeeks(prev => {
    const n = new Set(prev); n.has(wk) ? n.delete(wk) : n.add(wk); return n;
  });
  const toggleLot = (id) => setExpandedLots(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const isSupplier = data.mode === 'supplier';

  return (
    <div className="space-y-4" data-testid="external-analytics-tab">
      {/* Загрузка файлов */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex items-center gap-2 mb-2">
          <Upload className="w-4 h-4 text-blue-700" />
          <h3 className="text-sm font-semibold text-slate-900">Загрузить файлы «История предложений»</h3>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Поддерживается Excel (.xlsx). Берутся только строки, где в колонке «Действие» указано «Изменение цены позиции».
          Можно выбрать сразу несколько файлов или целую папку — данные будут объединены.
          Повторные загрузки дедуплицируются по содержимому.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="external-pick-files"
              className="h-8 text-xs rounded-sm">
              <Upload className="w-3.5 h-3.5 mr-1" />
              Выбрать файлы
            </Button>
            <input ref={fileInputRef} type="file" accept=".xlsx" multiple
              onChange={(e) => handleUpload(Array.from(e.target.files || []))}
              data-testid="external-upload-input"
              className="hidden" disabled={uploading} />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary"
              onClick={() => folderInputRef.current?.click()}
              disabled={uploading}
              data-testid="external-pick-folder"
              className="h-8 text-xs rounded-sm">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
              Выбрать папку
            </Button>
            <input ref={folderInputRef} type="file"
              webkitdirectory="" directory="" multiple
              onChange={(e) => handleUpload(Array.from(e.target.files || []))}
              data-testid="external-folder-input"
              className="hidden" disabled={uploading} />
            <span className="text-[10px] text-slate-400">
              .xlsx будут отобраны автоматически
            </span>
          </div>
          {uploading && (
            <span className="text-xs text-blue-700" data-testid="external-upload-progress">
              Загрузка{uploadProgress ? ` ${uploadProgress.done}/${uploadProgress.total}` : '…'}
            </span>
          )}
        </div>
        {batches.length > 0 && (
          <div className="mt-3 border-t border-slate-100 pt-3 space-y-1">
            <div className="text-[11px] text-slate-500 mb-1">Загруженные файлы:</div>
            {batches.map(b => (
              <div key={b.id} className="flex items-center gap-2 text-[11px] text-slate-700"
                data-testid={`batch-${b.id}`}>
                <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="truncate flex-1" title={b.source_file}>{b.source_file}</span>
                <span className="text-slate-400">{b.rows_total} строк · {(b.products || []).length} тов. · {(b.suppliers || []).length} пост.</span>
                <button onClick={() => deleteBatch(b.id)}
                  data-testid={`batch-delete-${b.id}`}
                  className="text-rose-400 hover:text-rose-700" title="Удалить">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Папка автоимпорта */}
      <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="external-watch-panel">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-amber-700" />
            <h3 className="text-sm font-semibold text-slate-900">Папка авто-импорта на сервере</h3>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${
              watch.enabled
                ? (watch.watcher_running ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800')
                : 'bg-slate-100 text-slate-600'
            }`}>
              {watch.enabled ? (watch.watcher_running ? 'Активен' : 'Запускается…') : 'Выключен'}
            </span>
          </div>
          <label className="text-[12px] text-slate-700 flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={!!watch.enabled}
              onCheckedChange={(v) => saveWatchSettings({ enabled: !!v })}
              data-testid="ext-watch-enabled"
            />
            <span className="font-medium">Авто-сканирование</span>
          </label>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Путь к папке <b>на самом сервере</b> (например <code className="bg-slate-100 px-1">/opt/truequote/external_files</code>),
          куда вы кладёте файлы через SFTP/SCP. Локальные/сетевые папки Windows
          (<code className="bg-slate-100 px-1">\\server\share</code>, <code className="bg-slate-100 px-1">C:\…</code>)
          сервер видеть не может — используйте «Выбрать папку» в блоке выше.
          Дубли пропускаются по содержимому, файлы не удаляются.
        </p>
        <div className="grid md:grid-cols-[1fr_120px_140px_auto_auto] gap-2 items-end">
          <div>
            <label className="text-xs text-slate-600">Путь к папке</label>
            <Input value={watchFolderInput}
              onChange={e => setWatchFolderInput(e.target.value)}
              placeholder="/opt/truequote/external_files"
              data-testid="ext-watch-folder"
              className="h-8 text-xs rounded-sm border-slate-300 font-mono" />
          </div>
          <Button size="sm" variant="outline" onClick={() => openBrowser('')}
            data-testid="ext-watch-browse"
            className="h-8 text-xs rounded-sm">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
            Обзор…
          </Button>
          <div>
            <label className="text-xs text-slate-600">Интервал, сек</label>
            <Input type="number" min={5} step={5}
              value={watchIntervalInput}
              onChange={e => setWatchIntervalInput(e.target.value)}
              data-testid="ext-watch-interval"
              className="h-8 text-xs rounded-sm border-slate-300" />
          </div>
          <Button size="sm" variant="secondary" onClick={() => saveWatchSettings()}
            data-testid="ext-watch-save"
            className="h-8 text-xs rounded-sm">
            Сохранить
          </Button>
          <Button size="sm" onClick={scanNow} disabled={scanBusy || !watch.folder_path}
            data-testid="ext-watch-scan-now"
            className="h-8 text-xs rounded-sm bg-blue-700 hover:bg-blue-800 text-white">
            {scanBusy ? 'Сканирую…' : 'Сканировать сейчас'}
          </Button>
        </div>
        <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap"
          data-testid="ext-watch-status">
          <span>Последнее сканирование: <b className="text-slate-700">
            {watch.last_scan_at
              ? new Date(watch.last_scan_at).toLocaleString('ru-RU')
              : '—'}
          </b></span>
          <span>· Импортировано: <b className="text-emerald-700">{watch.last_scan_imported || 0}</b></span>
          <span>· Пропущено: <b className="text-slate-600">{watch.last_scan_skipped || 0}</b></span>
          {watch.last_scan_error && (
            <span className="text-rose-600">· Ошибка: {watch.last_scan_error}</span>
          )}
          {watch.next_scan_at && watch.enabled && (
            <span>· Следующее: <b className="text-slate-700">
              {new Date(watch.next_scan_at).toLocaleTimeString('ru-RU')}
            </b></span>
          )}
        </div>
        {scanResult && Array.isArray(scanResult.files) && scanResult.files.length > 0 && (
          <div className="mt-3 border-t border-slate-100 pt-2 space-y-0.5 max-h-40 overflow-y-auto">
            {scanResult.files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                  f.status === 'imported' ? 'bg-emerald-500'
                  : f.status === 'duplicate' ? 'bg-slate-300'
                  : f.status === 'error' ? 'bg-rose-500' : 'bg-amber-400'}`} />
                <span className="truncate flex-1 text-slate-700" title={f.file}>{f.file}</span>
                <span className="text-slate-500 text-[10px]">
                  {f.status === 'imported' && `+${f.rows || 0} строк`}
                  {f.status === 'duplicate' && 'уже был'}
                  {f.status === 'empty' && (f.error || 'пусто')}
                  {f.status === 'error' && f.error}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Модалка обзора серверных папок */}
      {browseOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="ext-browse-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setBrowseOpen(false); }}>
          <div className="bg-white rounded-sm shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-amber-700" />
                Выберите папку на сервере
              </h3>
              <button onClick={() => setBrowseOpen(false)} className="text-slate-400 hover:text-slate-700"
                data-testid="ext-browse-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 py-2 border-b border-slate-100 bg-slate-50">
              <div className="text-[10px] text-slate-500 mb-1">Текущий путь:</div>
              <div className="font-mono text-xs text-slate-800 break-all" data-testid="ext-browse-path">
                {browseData?.path || '—'}
              </div>
              {browseData?.allowed_roots && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {browseData.allowed_roots.map(r => (
                    <button key={r} type="button"
                      onClick={() => loadBrowsePath(r)}
                      className="text-[10px] px-1.5 py-0.5 rounded-sm bg-white border border-slate-200 hover:bg-blue-50 hover:border-blue-300 text-slate-600 font-mono">
                      {r}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-2 py-2">
              {browseLoading ? (
                <div className="text-xs text-slate-400 p-3">Загрузка…</div>
              ) : browseError ? (
                <div className="text-xs text-rose-600 p-3" data-testid="ext-browse-error">{browseError}</div>
              ) : (
                <>
                  {browseData?.parent && (
                    <button type="button"
                      onClick={() => loadBrowsePath(browseData.parent)}
                      data-testid="ext-browse-up"
                      className="w-full text-left px-3 py-1.5 text-xs rounded-sm hover:bg-slate-50 flex items-center gap-2">
                      <ChevronDown className="w-3.5 h-3.5 rotate-180 text-slate-400" />
                      <span className="text-slate-500">.. (вверх)</span>
                    </button>
                  )}
                  {browseData?.xlsx_count_here > 0 && (
                    <div className="px-3 py-1 text-[10px] text-emerald-700">
                      В текущей папке: {browseData.xlsx_count_here} .xlsx файлов
                    </div>
                  )}
                  {browseData?.dirs?.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400 italic">Подпапок нет</div>
                  )}
                  {browseData?.dirs?.map(d => (
                    <button key={d.path} type="button"
                      onClick={() => loadBrowsePath(d.path)}
                      data-testid={`ext-browse-dir-${d.name}`}
                      className="w-full text-left px-3 py-1.5 text-xs rounded-sm hover:bg-blue-50 flex items-center gap-2">
                      <FileSpreadsheet className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="font-mono text-slate-800 truncate flex-1">{d.name}</span>
                      {d.xlsx_count > 0 && (
                        <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm shrink-0">
                          {d.xlsx_count} xlsx
                        </span>
                      )}
                      {d.xlsx_count < 0 && (
                        <span className="text-[10px] text-rose-600 shrink-0">нет доступа</span>
                      )}
                    </button>
                  ))}
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
              <Button size="sm" variant="ghost" onClick={() => setBrowseOpen(false)}
                className="h-8 text-xs rounded-sm">
                Отмена
              </Button>
              <Button size="sm" onClick={chooseBrowsedPath}
                disabled={!browseData?.path || !!browseError}
                data-testid="ext-browse-select"
                className="h-8 text-xs rounded-sm bg-blue-700 hover:bg-blue-800 text-white">
                Выбрать эту папку
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Фильтры */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <div className="space-y-1" ref={productMenuRef}>
            <label className="text-xs text-slate-600">Закупаемый товар</label>
            <div className="relative">
              <button type="button"
                onClick={() => setProductMenuOpen(o => !o)}
                data-testid="ext-product-trigger"
                className="w-full h-8 text-xs rounded-sm border border-slate-300 bg-white px-2 flex items-center justify-between gap-1">
                <span className="truncate text-left flex-1">
                  {selectedProducts.length === 0
                    ? <span className="text-slate-400">Все товары</span>
                    : selectedProducts.length === 1
                      ? selectedProducts[0]
                      : <span><b>{selectedProducts.length}</b> выбрано</span>}
                </span>
                {selectedProducts.length > 0 && (
                  <span role="button" onClick={(e) => { e.stopPropagation(); clearProducts(); }}
                    data-testid="ext-product-clear"
                    className="text-slate-400 hover:text-slate-700">
                    <X className="w-3.5 h-3.5" />
                  </span>
                )}
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
              {productMenuOpen && (
                <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto bg-white border border-slate-200 rounded-sm shadow-md"
                  data-testid="ext-product-menu">
                  <div className="sticky top-0 bg-slate-50 border-b border-slate-100 px-2 py-1 flex items-center justify-between text-[10px] text-slate-500">
                    <span>Выбрано: <b className="text-slate-700">{selectedProducts.length}</b> из {products.length}</span>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setSelectedProducts([...products])}
                        data-testid="ext-product-select-all"
                        className="hover:text-slate-900">Все</button>
                      <button type="button" onClick={clearProducts}
                        className="hover:text-slate-900">Сбросить</button>
                    </div>
                  </div>
                  {products.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-slate-400">Нет товаров — загрузите Excel выше</div>
                  ) : products.map(p => {
                    const checked = selectedProducts.includes(p);
                    return (
                      <label key={p}
                        data-testid={`ext-product-option-${p}`}
                        className={`flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50 ${checked ? 'bg-blue-50/40' : ''}`}>
                        <Checkbox checked={checked} onCheckedChange={() => toggleProduct(p)} />
                        <span className="flex-1 truncate" title={p}>{p}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {selectedProducts.length > 1 && (
              <div className="text-[10px] text-slate-500 mt-1">
                Аналитика по: {selectedProducts.join(', ')}
              </div>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-600">Период с</label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="h-8 text-xs rounded-sm border-slate-300" data-testid="ext-from" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-600">Период по</label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="h-8 text-xs rounded-sm border-slate-300" data-testid="ext-to" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <label className="text-[12px] text-slate-700 flex items-center gap-2 cursor-pointer select-none">
            <Checkbox checked={bySupplier} onCheckedChange={setBySupplier} data-testid="ext-by-supplier" />
            <span className="font-medium">Только по поставщику</span>
          </label>
          {bySupplier && (
            <div className="flex items-center gap-2">
              <Select value={supplier} onValueChange={setSupplier}>
                <SelectTrigger className="h-8 w-72 text-xs rounded-sm border-slate-300" data-testid="ext-supplier">
                  <SelectValue placeholder="Выберите поставщика…" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.length === 0
                    ? <div className="px-2 py-1.5 text-xs text-slate-400">Нет поставщиков</div>
                    : suppliers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {supplier && (
                <button type="button" onClick={() => setSupplier('')} className="text-slate-400 hover:text-slate-700">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* График */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        {loading ? (
          <div className="py-10 text-center text-xs text-slate-400">Загрузка…</div>
        ) : error ? (
          <div className="py-6 px-4 bg-red-50 border border-red-200 text-xs text-red-700 rounded-sm flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
          </div>
        ) : bySupplier && !supplier ? (
          <div className="py-6 px-4 bg-amber-50 border border-amber-200 text-xs text-amber-800 rounded-sm">
            Выберите поставщика, чтобы построить график по нему.
          </div>
        ) : bidSeries.points.length === 0 ? (
          <div className="py-6 px-4 bg-amber-50 border border-amber-200 text-xs text-amber-800 rounded-sm flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {batches.length === 0
                ? 'Загрузите Excel-файл выше, чтобы появились данные.'
                : 'Нет данных под выбранные фильтры.'}
            </span>
          </div>
        ) : (
          <>
            <div className="mb-2 text-[11px] text-slate-500">
              {isSupplier && (
                <span className="inline-block mr-2 px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded-sm font-semibold">
                  По поставщику: {supplier}
                </span>
              )}
              Ставок: <span className="font-semibold text-slate-700">{bidSeries.points.length}</span>
              {' · '}Поставщиков: <span className="font-semibold text-slate-700">{bidSeries.suppliers.length}</span>
              {' · '}Лотов: <span className="font-semibold text-slate-700">{data.tender_count}</span>
            </div>
            <div style={{ width: '100%', height: 380 }} data-testid="external-bids-chart">
              <ResponsiveContainer>
                <LineChart data={bidSeries.points} margin={{ top: 20, right: 28, left: 8, bottom: 8 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="ts"
                    type="number"
                    scale="time"
                    domain={tsDomain}
                    tickFormatter={fmtTs}
                    tick={{ fontSize: 11, fill: '#475569' }}
                    padding={{ left: 16, right: 16 }}
                  />
                  <YAxis domain={bidSeries.domain} tick={{ fontSize: 11, fill: '#475569' }}
                    tickFormatter={(v) => Number(v).toLocaleString('ru-RU')} />
                  <Tooltip content={<BidsTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {bidSeries.suppliers.map((sup, idx) => (
                    <Line
                      key={sup}
                      type="monotone"
                      dataKey={sup}
                      name={sup}
                      stroke={colorFor(idx)}
                      strokeWidth={2}
                      connectNulls
                      dot={{ r: 4, strokeWidth: 1, fill: colorFor(idx) }}
                      activeDot={{ r: 6 }}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Сводная таблица: неделя → лоты → поставщики */}
            <div className="mt-4 border-t border-slate-200 pt-3 overflow-x-auto">
              <table className="w-full text-[11px] tabular-nums">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-100">
                    <th className="w-6"></th>
                    <th className="text-left font-medium py-1.5 pr-3">Неделя (с)</th>
                    <th className="text-right font-medium py-1.5 px-3">Лотов</th>
                    <th className="text-right font-medium py-1.5 px-3 text-blue-700">Первая</th>
                    <th className="text-right font-medium py-1.5 px-3 text-green-700">Финал</th>
                    <th className="text-right font-medium py-1.5 pl-3 text-blue-700">Δ в торгах</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map(row => {
                    const auctionPct = row.first_winner > 0 ? ((row.first_winner - row.winning) / row.first_winner) * 100 : 0;
                    const isOpen = expandedWeeks.has(row.week_start);
                    return (
                      <Fragment key={row.week_start}>
                        <tr className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer select-none"
                          onClick={() => toggleWeek(row.week_start)}>
                          <td className="text-center text-slate-400">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5 inline" /> : <ChevronRight className="w-3.5 h-3.5 inline" />}
                          </td>
                          <td className="py-1.5 pr-3 text-slate-700 font-medium">
                            {new Date(row.week_start).toLocaleDateString('ru-RU')}
                          </td>
                          <td className="py-1.5 px-3 text-right text-slate-600">{row.tenders}</td>
                          <td className="py-1.5 px-3 text-right text-blue-700">{fmtNum(row.first_winner)}</td>
                          <td className="py-1.5 px-3 text-right text-green-700">{fmtNum(row.winning)}</td>
                          <td className="py-1.5 pl-3 text-right text-blue-700">{auctionPct.toFixed(2)}%</td>
                        </tr>
                        {isOpen && (row.lots || []).map(lot => {
                          const lotOpen = expandedLots.has(lot.tender_id);
                          const lotAuctionPct = lot.first_winner > 0 ? ((lot.first_winner - lot.winning) / lot.first_winner) * 100 : 0;
                          return (
                            <Fragment key={lot.tender_id}>
                              <tr className="bg-slate-50/60 border-b border-slate-100 cursor-pointer hover:bg-blue-50/40"
                                onClick={() => toggleLot(lot.tender_id)}>
                                <td className="text-center text-slate-300">
                                  {lotOpen ? <ChevronDown className="w-3 h-3 inline" /> : <ChevronRight className="w-3 h-3 inline" />}
                                </td>
                                <td colSpan={2} className="py-1 pr-3 text-slate-700 italic">
                                  {lot.lot_name}
                                </td>
                                <td className="py-1 px-3 text-right text-blue-700">{fmtNum(lot.first_winner)}</td>
                                <td className="py-1 px-3 text-right text-green-700">{fmtNum(lot.winning)}</td>
                                <td className="py-1 pl-3 text-right text-blue-700">{lotAuctionPct.toFixed(2)}%</td>
                              </tr>
                              {lotOpen && (lot.suppliers || []).map(s => (
                                <tr key={s.supplier} className="bg-white border-b border-slate-50">
                                  <td></td>
                                  <td colSpan={2} className="py-0.5 pr-3 pl-6 text-slate-600">
                                    <span className="text-slate-400">↳</span> {s.supplier}
                                    <span className="ml-2 text-[10px] text-slate-400">({s.changes} изм.)</span>
                                  </td>
                                  <td className="py-0.5 px-3 text-right text-slate-500">
                                    Первая: {fmtNum(s.first_price)}
                                  </td>
                                  <td className="py-0.5 px-3 text-right text-slate-700">
                                    Финал: <span className="font-semibold">{fmtNum(s.final_price)}</span>
                                  </td>
                                  <td className="py-0.5 pl-3 text-right text-emerald-700 font-semibold">
                                    −{s.improvement_pct}%
                                  </td>
                                </tr>
                              ))}
                            </Fragment>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-2 text-[10px] text-slate-400 italic">
                Клик по строке недели → лоты. Клик по лоту → детализация по поставщикам и %% улучшения от первой к финальной цене.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
